import React, { useRef, useEffect, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import pdfWorker from 'pdfjs-dist/build/pdf.worker.entry';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

function PDFRenderer({ file }) {
  const canvasRef = useRef(null);
  const [pdf, setPdf] = useState(null); // Store the PDF document
  const [numPages, setNumPages] = useState(0); // Total pages
  const [currentPage, setCurrentPage] = useState(1); // Current page
  const [loading, setLoading] = useState(false);
  const renderTaskRef = useRef(null); // Ref to track the current render task

  const renderPage = async (pageNumber) => {
    try {
      setLoading(true);

      // Cancel any ongoing rendering task
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }

      const page = await pdf.getPage(pageNumber);

      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      const canvasWidth = canvas.parentElement.offsetWidth;
      const viewport = page.getViewport({ scale: 1 });
      const scale = canvasWidth / viewport.width;
      const scaledViewport = page.getViewport({ scale });

      const devicePixelRatio = window.devicePixelRatio || 1;
      canvas.width = scaledViewport.width * devicePixelRatio;
      canvas.height = scaledViewport.height * devicePixelRatio;
      canvas.style.width = `${scaledViewport.width}px`;
      canvas.style.height = `${scaledViewport.height}px`;

      context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);

      const renderContext = {
        canvasContext: context,
        viewport: scaledViewport,
      };

      const renderTask = page.render(renderContext);
      renderTaskRef.current = renderTask;

      // Wait for rendering to complete
      await renderTask.promise;
      setLoading(false);
    } catch (error) {
      if (error.name !== 'RenderingCancelledException') {
        
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadPDF = async () => {
      try {
        setLoading(true);

        if (!file) {
          throw new Error('No file provided.');
        }

        const arrayBuffer = await file.arrayBuffer();
        const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
        const loadedPdf = await loadingTask.promise;

        setPdf(loadedPdf);
        setNumPages(loadedPdf.numPages);

        // Render the first page by default
        await renderPage(1);
      } catch (error) {
        setLoading(false);
      }
    };

    loadPDF();
  }, [file]);

  useEffect(() => {
    if (pdf) {
      renderPage(currentPage);
    }
  }, [currentPage, pdf]);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < numPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  return (
    <div className="relative w-full border border-sky-400 shadow-lg rounded-lg overflow-hidden">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-50">
          <span className="loader" /> Loading...
        </div>
      )}
      <canvas ref={canvasRef} className="block"></canvas>
      <div className="flex items-center justify-between p-4 bg-gray-100 border-t">
        <button
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
          className="px-4 py-2 bg-blue-500 text-white rounded disabled:opacity-50"
        >
          Previous
        </button>
        <div>
          Page {currentPage} of {numPages || '...'}
        </div>
        <button
          onClick={handleNextPage}
          disabled={currentPage === numPages}
          className="px-4 py-2 bg-blue-500 text-white rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}

export default PDFRenderer;
